<template>
    <div class="product-grid-report">
        <div class="row main-content">
            <div class="keywords-content">
                <div class="row">
                    <div class="keywords-chart-holder loadable">
                        <h4 class="section-title">Estimated Clicks</h4>
                        <div v-if="isChartLoading"
                            class="loader-holder">
                            <base-loader class="chart-loader" />
                        </div>
                        <GChart v-else
                            :version="'42'"
                            :settings="{ packages: ['corechart'] }"
                            type="BarChart"
                            :data="estimatedClicksChartData"
                            :options="chartOptions"
                            class="keywords-chart"
                            ref="chart" />
                        <!-- chart section
                    {{ keywordChartData }}
                    <hr>
                    {{ competitorShops }}
                    <hr>
                    {{ mappedKeywordChartData }} -->
                    </div>
                    <div class="keywords-chart-holder loadable">
                        <h4 class="section-title">Total Cards</h4>
                        <div v-if="isChartLoading"
                            class="loader-holder">
                            <base-loader class="chart-loader" />
                        </div>
                        <GChart v-else
                            :version="'42'"
                            :settings="{ packages: ['corechart'] }"
                            type="BarChart"
                            :data="totalCardsChartData"
                            :options="chartOptions"
                            class="keywords-chart"
                            ref="chart" />
                    </div>
                </div>
                <div class="row product-grid-report__advanced">
                    <advanced-filters ref="advancedFilters"
                        :options="advancedOptions"
                        :predefinedOptions="_advancedFilters"
                        copyPage
                        exportEnabled
                        :regularFilterConfig="regularFilterConfig"
                        @share="copyFilters"
                        @export="exportCsv"
                        @apply-advanced="applyAdvancedOptions" />
                </div>
                <div class="keywords_table loadable">
                    <div class="table-header">
                        <h4 class="section-title">Keywords</h4>
                    </div>
                    <div v-if="isTableLoading"
                        class="loader-holder">
                        <base-loader class="chart-loader" />
                    </div>
                    <div class="table-holder">
                        <div class="table-filters">
                            <div class="table-filters__section">
                                <div class="table-filters__keyword-filter">
                                    <base-select isRotation
                                        :options="keywordFilterOptions"
                                        :selectStyle="{ width: '200px' }"
                                        v-model="filterParams.keyword.operator" />
                                    <label class="table-filters__input-container">
                                        <base-material-design-icon class="table-filters__search-icon"
                                            icon="search" />
                                        <input v-model="keywordFilter"
                                            class="table-filters__input"
                                            type="text"
                                            :placeholder="searchFieldPlaceholder" />

                                    </label>
                                </div>
                            </div>
                            <div class="table-filters__section">
                                <div class="date-filter">
                                    <label>Report Date:</label>
                                    <base-select v-model="filterParams.snapshot_date"
                                        class="date-select date-select-primary"
                                        :options="snapshots.map(date => ({ label: date, value: date }))"
                                        isRotation
                                        :show-top-border="true">
                                    </base-select>
                                </div>
                                <BaseApplicableMultiSelect :options="competitors"
                                    autoApply
                                    v-model="selectedCompetitors"
                                    :title="competitorsControlLabel"
                                    keepTitle />

                                <div class="table-filters__page-size">
                                    <base-select v-model="pageSize"
                                        :options="pageSizeOptions" />
                                </div>
                            </div>
                        </div>
                        <div id="keywords_table"></div>
                        <div id="table_footer"
                            class="table-custom-footer"
                            v-show="!isTableLoading"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { baseComponent } from '@/features/reports/common/Mixins'
import BaseSortMixins from '@/common/BaseSortMixins'
import { mapActions, mapState } from 'vuex'
import filters from '@/common/Filters'
import { GChart } from 'vue-google-charts'
import Tabulator from 'tabulator-tables'

import {
    addOptionsToConfig,
    ADVANCED_OPTION_TYPE,
    convertAdvancedOptionsToFilters,
    productGridFieldsConfig,
} from '@/features/reports/common/advanced-options/config'
import AdvancedFilters from '@/features/reports/common/advanced-filters/AdvancedFilters'
import { advancedFiltersMixins } from '@/features/reports/common/AdvancedFiltersMixins'

const ADVANCED_OPTIONS_CONFIG = productGridFieldsConfig

const REGULAR_FILTER_CONFIG = [
    {
        type: 'attribute',
        searchPlaceholder: 'Search attributes',
        listTitle: 'Include the following attributes:'
    },
    {
        type: 'urlAttribute',
        searchPlaceholder: 'Search url attributes',
        listTitle: 'Include the following url attributes:'
    },
    {
        type: 'pageType',
        searchPlaceholder: 'Search page types',
        listTitle: 'Include the following page types:'
    },
    {
        type: 'section',
        searchPlaceholder: 'Search sections',
        listTitle: 'Include the following sections:'
    },
    {
        type: 'gridNumber',
        searchPlaceholder: 'Search grid numbers',
        listTitle: 'Include the following grid numbers:'
    },
]

const KEYWORD_FILTER_VALUE_ENUMS = {
    Exactly: 'equal',
    Containing: 'contains',
    Begins: 'starts_with',
    Ends: 'ends_with'
}

const KEYWORD_FILTER_OPTIONS = [
    {
        value: KEYWORD_FILTER_VALUE_ENUMS.Exactly,
        label: 'Exactly matching'
    },
    {
        value: KEYWORD_FILTER_VALUE_ENUMS.Containing,
        label: 'Containing'
    },
    {
        value: KEYWORD_FILTER_VALUE_ENUMS.Begins,
        label: 'Begins with'
    },
    {
        value: KEYWORD_FILTER_VALUE_ENUMS.Ends,
        label: 'Ends with'
    }
]

const EMPTY_FILTERS = {
    keyword: {
        operator: 'contains',
        value: ''
    },
    snapshot_date: null,
}

function columnSum(columnData) {
    const sum = columnData.reduce((sum, item) => sum + item, 0);
    return sum ? sum.toLocaleString('en-US') : 0;
}

export default {
    name: 'ProductGridReportProgress',
    filters: {
        ...filters
    },
    components: { AdvancedFilters, GChart },
    mixins: [baseComponent, BaseSortMixins, advancedFiltersMixins],
    props: ['filters'],
    data() {
        return {
            selectedAdvancedOptions: [],
            advancedOptionsConfig: ADVANCED_OPTIONS_CONFIG,
            regularFilterConfig: REGULAR_FILTER_CONFIG,
            table: null,
            pageSize: 100,
            isChartLoading: false,
            isTableLoading: false,
            selectedCompetitors: [],
            selectedMetric: 'keyword_count',
            firstLoad: true,
            keywordFilterOptions: _.cloneDeep(KEYWORD_FILTER_OPTIONS),
            keywordFilter: '',
            keywordFilterTimer: null,
            filterParams: _.cloneDeep(EMPTY_FILTERS),
            lastAppliedKeywordSearch: '',
            chartOptions: {
                legend: {
                    position: 'none',
                },
                hAxis: {
                    format: 'short',
                },
                vAxis: {
                    textStyle: {
                        fontSize: 14,
                        bold: true
                    },
                    textPosition: 'out',
                },
                chartArea: {
                    left: '25%',
                    width: '75%',
                    height: '80%'
                },
                width: '100%',
            },
            tableSort: {
                column: 'msv',
                dir: 'desc'
            },
            currentTableRowsAmount: 0,
            pageSizes: [100, 250, 500],
        }
    },
    computed: {
        ...mapState('productGridReport', [
            'keywordChartData',
            'clientShop',
            'competitorShops',
            'keywordTableData'
        ]),
        ...mapState('rankZoneCommon', {
            snapshots: state => state.snapshots
        }),
        ...mapState('productGridReport', {
            estimatedClicksChartData(state) {
                if (!state.keywordChartData?.estimated_clicks) {
                    return null
                }
                const competitorsChartData = this.prepareCompetitorsChartData(state.keywordChartData.estimated_clicks)
                if (!competitorsChartData) {
                    return null
                }
                return [
                    ['Shop', 'Estimated Clicks', { role: 'style' }],
                    ...competitorsChartData
                ]
            },
            totalCardsChartData(state) {
                if (!state.keywordChartData?.total_cards) {
                    return null
                }
                const competitorsChartData = this.prepareCompetitorsChartData(state.keywordChartData.total_cards)
                if (!competitorsChartData) {
                    return null
                }
                return [
                    ['Shop', 'Total Cards', { role: 'style' }],
                    ...competitorsChartData
                ]
            }
        }),
        ...mapState(['selectedClient']),
        ...mapState('rankZoneCommon', {
            snapshots: state => state.snapshots
        }),

        ...mapState('rankZoneCommon', {
            advancedOptions(state) {
                const gridNumberOptions = [
                    { label: '1', value: '1', _id: '1' },
                    { label: '2', value: '2', _id: '2' },
                    { label: '3', value: '3', _id: '3' },
                    { label: '4+', value: '4+', _id: '4+' },
                ]
                let config = _.cloneDeep(this.advancedOptionsConfig)
                addOptionsToConfig(config, state.sections, ADVANCED_OPTION_TYPE.SECTION)
                addOptionsToConfig(config, gridNumberOptions, ADVANCED_OPTION_TYPE.GRID_NUMBER)
                addOptionsToConfig(config, state.attributes, ADVANCED_OPTION_TYPE.ATTRIBUTE)
                // addOptionsToConfig(config, state.urlStatuses, ADVANCED_OPTION_TYPE.URL_STATUS)
                addOptionsToConfig(config, state.pageTypes, ADVANCED_OPTION_TYPE.PAGE_TYPE)
                // addOptionsToConfig(config, state.scores, ADVANCED_OPTION_TYPE.SCORE)
                // addOptionsToConfig(config, state.isOptimized, ADVANCED_OPTION_TYPE.IS_OPTIMIZED)
                addOptionsToConfig(config, state.urlAttributes, ADVANCED_OPTION_TYPE.URL_ATTRIBUTE)
                // addOptionsToConfig(config, state.serpFeature, ADVANCED_OPTION_TYPE.SERP_FEATURE)
                // addOptionsToConfig(config, state.contentBriefStatuses, ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS)
                // addOptionsToConfig(config, state.mappedUrls, ADVANCED_OPTION_TYPE.URL)
                // addOptionsToConfig(config, state.mismatchedUrls, ADVANCED_OPTION_TYPE.MISMATCHED_URLS)

                return state.difficultyScoreEnabled ? config : config.filter(i => i.type !== ADVANCED_OPTION_TYPE.DIFF_SCORE)
            },
        }),
        competitors() {
            return this.competitorShops.map(shop => ({
                label: shop,
                value: shop,
            }))
        },
        competitorsControlLabel() {
            return `Comparing ${this.selectedCompetitors.length} shop${this.selectedCompetitors.length > 1 ? 's' : ''}`
        },
        pageSizeOptions() {
            return this.pageSizes.map(size => ({
                label: size,
                value: size,
            }))
        },
        searchFieldPlaceholder() {
            const selectedOperator = this.keywordFilterOptions.find(option => option.value === this.filterParams.keyword.operator)
            if (!selectedOperator) {
                return `search keywords`
            }
            return `search keywords ${selectedOperator.label.toLowerCase()}`
        },
        hiddenCompetitors() {
            return this.competitorShops.filter(shop => !this.selectedCompetitors.includes(shop))
        }
    },
    watch: {
        selectedClient(client) {
            if (!client) {
                return
            }
            this.isDataFetched = false
            this.isDataUpdating = true

            // Get sellers list first
            const filters = Object.assign({}, this.filterParams, {
                advancedFilters: convertAdvancedOptionsToFilters(this.selectedAdvancedOptions),
            })

            this.getSellersList({
                clientId: this.clientId,
                filters
            }).then(() => {
                this.loadPageData()
            })
        },
        competitorShops() {
            this.selectedCompetitors = _.cloneDeep(this.competitorShops.slice(0, 5))
        },
        keywordTableData: {
            handler() {
                this.mapTableData()
            },
            immediate: true,
        },
        snapshots: {
            handler(newSnapshots) {
                if (newSnapshots && newSnapshots.length > 0) {
                    this.filterParams.snapshot_date = newSnapshots[0]
                }
            },
            immediate: true
        },
        selectedCompetitors() {
            this.toggleTableColumns()
        },
        keywordFilter(v) {
            clearTimeout(this.keywordFilterTimer)
            this.keywordFilterTimer = setTimeout(() => {
                this.filterParams.keyword.value = v
                this.applyKeywordFilter()
            }, 1000)
        },
        pageSize(value) {
            this.table.setPageSize(value)
        },
        'filterParams.keyword.operator': {
            handler() {
                this.applyKeywordFilter()
            },
        },
        'filterParams.snapshot_date': {
            handler() {
                if (!this.selectedClient) {
                    return
                }

                this.loadPageData()
            }
        }
    },
    created() {
        if (this.selectedClient) {
            this.loadPageData()
        }
    },
    mounted() {
        this.selectedCompetitors = _.cloneDeep(this.competitorShops.slice(0, 5))
        this.table = new Tabulator('#keywords_table', {
            nestedFieldSeparator: ':', // dots in shop names (ie acme.com) were interpreted as nested fields
            columns: [],
            layout: 'fitColumns',
            tooltipsHeader: true,
            pagination: 'local',
            paginationSize: this.pageSize,
            footerElement: '#table_footer',
            movableColumns: true,
        })

        window.table = this.table

        this.$nextTick(() => {

            this.mapTableData()
        })
    },
    methods: {
        ...mapActions('productGridReport', [
            'getKeywordsChartData',
            'getKeywordsTableData',
            'getSellersList'
        ]),
        exportCsv() {
            const frozenRows = this.table.getRows().filter(row => row.isFrozen());
            frozenRows.forEach(row => {
                row.unfreeze()
            });
            let downloadName = `${this.selectedClient.name}_${this.filterParams.snapshot_date}`
            this.table.download('csv', `${downloadName}.csv`)
            frozenRows.forEach(row => {
                row.freeze()
            });
        },
        handleChartError(error) {
            // google.visualization.errors.removeError(error.id);
            console.error('vue-google-charts error:', error)
        },
        async loadPageData() {
            const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
            const filters = Object.assign({}, this.filterParams, {
                advancedFilters,
            })

            // Set loading states
            this.isChartLoading = true
            this.isTableLoading = true

            // Load all data
            const requests = [
                this.getKeywordsChartData({
                    clientId: this.clientId,
                    filters
                }).finally(() => {
                    this.isChartLoading = false
                }),
                this.getKeywordsTableData({
                    clientId: this.clientId,
                    filters
                }).finally(() => {
                    this.isTableLoading = false
                })
            ]
            if (!this.clientShop) {
                requests.push(this.getSellersList({
                    clientId: this.clientId,
                    filters
                }))
            }
            await Promise.all(requests)

            this.isDataFetched = true
        },
        async updateData() {
            await this.loadPageData()
        },
        async applyAdvancedOptions(options) {
            console.log('Advanced filters changed:', options)
            this.selectedAdvancedOptions = _.cloneDeep(options)
            
            // Update the AdvancedFilters component's applied options to show chips
            if (this.$refs.advancedFilters) {
                this.$refs.advancedFilters.setAppliedOptions(options)
            }
            
            this.loadPageData()
        },
        onWebPropertyGroupChanged(group) {
            this.webPropertyGroupSelected = group
            this.updateData()
        },
        prepareCompetitorsChartData(data) {

            const clientData = data.find(item => item.shop === this.clientShop)
            const restData = data.filter(item => item.shop !== this.clientShop)

            if (!clientData) {
                return null
            }

            const selectedCompetitorsData = this.selectedCompetitors.length ?
                restData.filter(item => this.selectedCompetitors.includes(item.shop)) :
                []
            return [
                [this.clientShop, clientData.value, '#2EC4B6'],
                ...selectedCompetitorsData.map(item => [item.shop, item.value, '#A0E4DD'])
            ]
        },
        applyKeywordFilter() {
            const keywordValue = this.filterParams.keyword.value
            const operator = this.filterParams.keyword.operator

            // Remove any existing keyword filters from advanced filters
            this.selectedAdvancedOptions = this.selectedAdvancedOptions.filter(
                option => option.selectedField !== 'keyword'
            )

            if (!keywordValue || keywordValue.length < 3) {
                // If no keyword value or too short, just apply current advanced filters without keyword
                this.applyAdvancedOptions(this.selectedAdvancedOptions)
                return
            }

            // Add keyword filter to advanced filters
            const keywordFilter = {
                id: Date.now().valueOf(),
                type: 'keyword',
                label: 'Keyword',
                selectedField: 'keyword',
                selectedOperator: operator,
                selectedFilter: {
                    text: keywordValue,
                    value: keywordValue
                },
                isFirst: this.selectedAdvancedOptions.length === 0,
                operators: {
                    type: 'string',
                    operatorVariants: [
                        { label: 'Exactly matching', value: 'equal' },
                        { label: 'Containing', value: 'contains' },
                        { label: 'Begins with', value: 'starts_with' },
                        { label: 'Ends with', value: 'ends_with' }
                    ]
                }
            }

            // Add the keyword filter to advanced options
            this.selectedAdvancedOptions.push(keywordFilter)

            // Apply the updated advanced filters
            this.applyAdvancedOptions(this.selectedAdvancedOptions)
        },
        mapTableData() {
            if (!this.keywordTableData?.keywords) {
                return
            }
            const data = this.keywordTableData
            this.currentTableRowsAmount = data.keywords.length

            if (!this.table) {
                return
            }

            const tableColumns = [
                {
                    title: 'Keyword',
                    field: 'keyword',
                    frozen: true,
                    headerSort: true,
                    formatter: (cell) => {
                        const value = cell.getValue();
                        const query = encodeURI(value)
                        return `<a href="https://www.google.com/search?q=${query}" target="_blank" class="keyword-link">${value} 
                                    <svg fill="currentColor" width="14" height="14" viewBox="0 0 24 24" class="material-design-icon__svg"><path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"><title></title></path></svg>
                                </a>`
                    },
                    sorter: 'string',
                    minWidth: 250,
                    movable: false,
                    topCalc: () => {
                        const totalItems = this.table.getDataCount()
                        const filteredRows = this.table.getDataCount('active')
                        const percent = filteredRows / totalItems * 100
                        const roundedPercent = Math.round(percent * 100) / 100

                        return {
                            filteredRows,
                            totalItems,
                            percent: roundedPercent,
                        }
                    },
                    topCalcFormatter: (cell) => {
                        const value = cell.getValue();
                        return `<div class="total-keywords-cell">
                                ${value.filteredRows} / ${value.totalItems} <span class="percent">${value.percent}%</span>
                            </div>`
                    },
                },
                {
                    title: 'MSV',
                    field: 'msv',
                    headerSort: true,
                    frozen: true,
                    formatter: (cell) => cell.getValue().toLocaleString('en-US'),
                    sorter: 'number',
                    minWidth: 80,
                    topCalc: columnSum,
                },
                {
                    title: 'Total Cards',
                    field: 'total_cards',
                    headerSort: true,
                    frozen: true,
                    sorter: 'number',
                    minWidth: 120,
                    topCalc: columnSum,
                },
                {
                    title: data.client_shop,
                    field: data.client_shop,
                    headerSort: true,
                    sorter: 'number',
                    widthGrow: 1,
                    topCalc: columnSum,
                },
                ...data.competitor_shops
                    .map(shop => ({
                        title: shop,
                        field: shop,
                        headerSort: true,
                        sorter: 'number',
                        widthGrow: 1,
                        topCalc: columnSum,
                        visible: !this.hiddenCompetitors.includes(shop)
                    }))
            ]
            this.table.setColumns(tableColumns)
            this.table.setSort([{ ...this.tableSort }])
            this.table.setData(data.keywords)
        },
        toggleTableColumns() {
            const tableColumns = this.table.getColumns()
            tableColumns.forEach(column => {
                const field = column.getField()
                if (this.hiddenCompetitors.includes(field)) {
                    this.table.hideColumn(field)
                } else {
                    this.table.showColumn(field)
                }
            })
        },
        /**
         * Add a new advanced filter programmatically
         * @param {Object} filterConfig - Configuration for the new filter
         * @param {string} filterConfig.field - The field type (e.g., 'attribute', 'section', etc.)
         * @param {string} filterConfig.operator - The operator (e.g., 'equal', 'contains', etc.)
         * @param {string|Object} filterConfig.value - The filter value
         * @param {boolean} filterConfig.autoApply - Whether to automatically apply the filter (default: true)
         */
        addAdvancedFilter(filterConfig) {
            if (!this.$refs.advancedFilters) {
                console.warn('AdvancedFilters component not found')
                return
            }

            const { field, operator, value, autoApply = true } = filterConfig

            // Find the field configuration from available options
            const fieldOption = this.advancedOptions.find(option => option.type === field)
            if (!fieldOption) {
                console.warn(`Field type '${field}' not found in available options`)
                return
            }

            // Create a new filter object
            const newFilter = {
                id: Date.now().valueOf(),
                ...fieldOption,
                selectedField: field,
                selectedOperator: operator,
                selectedFilter: {
                    text: typeof value === 'string' ? value : value.text || '',
                    value: typeof value === 'string' ? value : value.value || value
                },
                isFirst: this.selectedAdvancedOptions.length === 0
            }

            console.log('Adding new advanced filter:', newFilter)

            // Add the filter to the current selected options
            this.selectedAdvancedOptions.push(newFilter)

            // If autoApply is true, trigger the apply method
            if (autoApply) {
                this.$nextTick(() => {
                    this.applyAdvancedOptions(this.selectedAdvancedOptions)
                })
            }
        },
        /**
         * Clear all advanced filters programmatically
         * @param {boolean} autoApply - Whether to automatically apply the cleared state (default: true)
         */
        clearAdvancedFilters(autoApply = true) {
            if (!this.$refs.advancedFilters) {
                console.warn('AdvancedFilters component not found')
                return
            }

            console.log('Clearing all advanced filters')

            // Clear the selected options
            this.selectedAdvancedOptions = []

            // If autoApply is true, trigger the apply method
            if (autoApply) {
                this.$nextTick(() => {
                    this.applyAdvancedOptions(this.selectedAdvancedOptions)
                })
            }
        },
    },
}
</script>

<style scoped lang="scss">
.container {
    display: flex;
    flex-direction: column;
    width: 90vw;
    margin: 0 auto;
}

.section-row {
    border-bottom: 2px solid $rfx-light-steel;
    display: flex;
    flex-direction: row;
    height: 40px;
    width: 100%;
}

.section-title {
    margin: 0;
    font-size: 16px;
    padding-bottom: 16px;
    display: block;
    font-weight: 500;
}

.section-filler {
    width: 100%;
}

.row {
    display: flex;
    flex-direction: row;
    gap: 16px;
}

.product-grid-report {
    min-width: 1200px;
    position: relative;
    min-height: 200vh !important;

    &__advanced {
        margin-top: 30px;
        margin-bottom: 14px;
        position: relative;

        &::v-deep .collapsible__header {
            padding-right: 80px !important;
        }

        &::v-deep .collapsible__arrow {
            right: 66px !important;
        }
    }
}

.main-content {
    width: 100%;
    display: flex;
    flex-direction: column;
}

.loadable {
    position: relative;
}

.loader-holder {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, .7);
}

.keywords-chart-holder {
    width: 100%;
    height: 100%;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    background-color: #fff;
    flex: 1;
    height: 400px;
    border-radius: 4px;
    border: 1px solid $rfx-light-steel;
}

.keywords-chart {
    flex: 1 1 100%;
    position: relative;
    padding: 0 16px;
}

.keywords_table {
    width: 100%;
    background-color: #fff;
    margin-top: 10px;
    border-radius: 4px;
    border: 1px solid $rfx-light-steel;
    padding: 16px;
    box-sizing: border-box;
}

/* Tabulator styles */
::v-deep(.tabulator .tabulator-cell) {
    border-right: none !important;
}

::v-deep(.tabulator .tabulator-header .tabulator-col) {
    border-right: none !important;
}

::v-deep(.tabulator-cell) {
    height: 40px !important;
    align-content: center !important;
}

::v-deep(.tabulator-row .tabulator-cell:last-child),
::v-deep(.tabulator-header .tabulator-col:last-child) {
    border-right: none !important;
}

::v-deep(.tabulator .tabulator-col.tabulator-sortable[aria-sort="none"] .tabulator-col-sorter) {
    opacity: 0;
}

::v-deep(.tabulator-row.tabulator-selectable:hover) {
    background-color: #fff !important;
}

::v-deep(.tabulator .tabulator-header .tabulator-col-title) {
    padding-right: 15px !important;
    text-transform: capitalize !important;
}

::v-deep .tabulator {
    .tabulator-col-title {
        height: 40px;
        line-height: 40px;
        padding-left: 8px;
        padding-right: 8px;
    }

    .tabulator-cell {
        padding-left: 8px;
        padding-right: 8px;
        height: 48px !important;
    }

    .total-keywords-cell .percent {
        display: inline-block;
        padding-left: 25px;
    }

    .tabulator-header {

        .tabulator-calcs-holder,
        .tabulator-calcs-holder .tabulator-row {
            background: #fff !important;
        }
    }

    .tabulator-calcs-top {
        border-top: 2px solid $rfx-color-blue-600;
        border-bottom: 1px solid $rfx-color-border-color-hover;
        background-color: #fff !important;
        color: $rfx-color-neutral-800;
    }

    &-paginator,
    &-pages {
        display: flex;
        justify-content: center;
        gap: 8px;
    }

    &-paginator>button:not(:disabled) {
        color: $rfx-color-neutral-800;
    }

    &-page {
        border: none;
        background: #fff;

        &.active {
            color: $rfx-cyan;
        }
    }
}

.chart-header {
    width: 100%;
    justify-content: flex-end;
}

.table-filters {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;

    &__section {
        display: flex;
        align-items: center;
        gap: 32px;

        &>*:not(:first-child) {
            position: relative;

            &::before {
                content: '';
                position: absolute;
                left: -16px;
                top: 0;
                height: 100%;
                width: 1px;
                background-color: $rfx-border-color;
            }
        }
    }

    &__keyword-filter {
        display: flex;
        align-items: center;
    }

    &__input-container {
        display: flex;
        align-items: center;
        border: 1px solid $rfx-color-neutral-200;
        border-left-width: 0;
        height: 40px;
        padding: 0 15px;
        gap: 8px;
        width: 300px;
        box-sizing: border-box;
    }

    &__input {
        border: none !important;
        background-color: #fff;
        flex: 1 1 100%;
        padding: 0;
        outline: none;
    }

    &__search-icon {
        font-size: 15px;
        color: $rfx-text-dark-high-contrast;
        pointer-events: none;
    }
}

.date-filter {
    display: flex;
    align-items: center;
    gap: 10px;

    label {
        color: $rfx-dark-march;
        font-weight: 500;
    }

    .date-select-primary {
        width: 200px;
    }

    .date-select-compare {
        width: 200px;

        ::v-deep .select__selection {
            position: relative;
            margin-left: 10px;

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: $rfx-cyan;
            }
        }
    }
}

::v-deep .keyword-link {
    color: $rfx-text-dark-high-contrast;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }

    svg {
        opacity: .35;
        display: inline-block;
        margin-bottom: -2px;
    }
}

.table-custom-footer {
    margin: 36px -16px -87px;
    border: 1px solid $rfx-color-border-color;
    padding: 16px 0;
}
</style>